import { Reservation } from "@workspace/domain/src/features/reservation";
import { errorMessage } from "@workspace/domain/src/utils";
import Decimal from "decimal.js";
import * as z from "zod";
import { ClientTrip } from "../trip/schema";

const SortBy = ["createdAt", "startDate", "totalPrice", "status"] as const;
const SortOrder = ["asc", "desc"] as const;

const ReservationSchema = z.object({
  id: z.string(),

  serial: z.string(),
  status: z.enum(Reservation.Status),
  cancelledAt: z.date().nullable(),

  // Trip information
  groupPricing: ClientTrip.Trip.shape.groupPricing,
  adultPrice: z.number(),
  adultReservationFee: z.number(),
  adultCancellationFee: z.number().nullable(),
  childPrice: z.number(),
  childReservationFee: z.number(),
  childCancellationFee: z.number().nullable(),
  cancellationDeadline: z.date().nullable(),

  numberOfAdults: z.number(),
  numberOfChildren: z.number(),

  totalPrice: z.number(),
  totalReservationFee: z.number(),
  totalCancellationFee: z.number(),
  totalRefundable: z.number(),

  totalPaid: z.number(),
  totalRefunded: z.number(),

  amountDue: z.number(),
  refundDue: z.number(),

  tripId: z.string(),
  agencyClientId: z.string(),

  createdAt: z.date(),
  updatedAt: z.date(),
});

const GetSchema = z.object({
  id: z.string(),
});

const BaseSchema = z.object({
  tripId: z.string(),

  numberOfAdults: z
    .number(errorMessage("Number of adults is required", "Number of adults is invalid"))
    .min(1, "Number of adults must be at least 1")
    .max(100, "Number of adults must be less than 100"),
  numberOfChildren: z
    .number(errorMessage("Number of children is required", "Number of children is invalid"))
    .min(0, "Number of children must be at least 0")
    .max(100, "Number of children must be less than 100"),

  encryptedKey: z.string().nullable(),
  discountCode: z.string().optional(),

  totalPrice: z.number(),
  totalReservationFee: z.number(),
  totalCancellationFee: z.number(),
});

const GetAllSchema = z.object({
  sortBy: z.enum(SortBy),
  sortOrder: z.enum(SortOrder),
  search: z.string().optional(),
  status: z.enum(Reservation.Status).optional(),
  dateMin: z.date().optional(),
  dateMax: z.date().optional(),
  totalPriceMin: z
    .number()
    .transform((val) => new Decimal(val).times(100).toNumber())
    .optional(),
  totalPriceMax: z
    .number()
    .transform((val) => new Decimal(val).times(100).toNumber())
    .optional(),
});

const CreateSchema = BaseSchema;

const PaymentSchema = z.object({
  id: z.string(),
  status: z.enum(Reservation.PaymentStatus),
  amount: z.number(),
  sourceSubType: z.string().nullable(),
  sourcePan: z.string().nullable(),
  transactionId: z.string().nullable(),
  reservationId: z.string(),
  createdAt: z.date(),
});

const GetPaymentUrlSchema = z.object({
  reservationId: z.string(),
});

export const ClientReservation = {
  Reservation: ReservationSchema,
  Get: GetSchema,
  Create: CreateSchema,
  GetAll: GetAllSchema,
  Payment: PaymentSchema,
  GetPaymentUrl: GetPaymentUrlSchema,
};
export type ClientReservation = {
  Reservation: z.infer<typeof ReservationSchema>;
  Get: z.infer<typeof GetSchema>;
  Create: z.infer<typeof CreateSchema>;
  GetAll: z.infer<typeof GetAllSchema>;
  Payment: z.infer<typeof PaymentSchema>;
  GetPaymentUrl: z.infer<typeof GetPaymentUrlSchema>;
};
